import React, { useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import classnames from 'classnames'
import hasElementOrSomeParentTheClass from '@Utils/HasElementOrSomeParentTheClass'
import Overlay from '@SiteComponents/Overlay'
import SpeakerVoiceSidePanelContent from './SpeakerVoiceSidePanelContent'
import Close from '@Images/languages_view_close.svg'
import Expand from '@Images/languages_view_expand.svg'
import Next from '@Images/languages_view_next.svg'
import Previous from '@Images/languages_view_previous.svg'

import {
  removeSpeakerVoicesFromWorkspace,
} from '@SiteModules/SpeakerVoice/actions'

import {
  ISpeakerVoice,
} from '@SiteModules/SpeakerVoice/types'

import {
  getSpeakerVoices,
} from '@SiteModules/SpeakerVoice/selectors'

import './SpeakerVoiceSidePanel.scss'

interface SpeakerVoiceSidePanelProps {
  readonly currentWorkspaceId: number
  readonly speakerVoice?: ISpeakerVoice
  readonly isNewSpeakerVoice: boolean
  readonly fullSpeakerVoiceSidePanel: boolean
  readonly shouldRender: boolean
  resetCurrentSpeakerVoice(): void
  setCurrentSpeakerVoice(speakerVoice: ISpeakerVoice): void
  resetIsNewCurrentSpeakerVoice(): void
  setFullSpeakerVoiceSidePanel(newFullSpeakerVoiceSidePanel: boolean): void
}

const SpeakerVoiceSidePanel: React.FC<SpeakerVoiceSidePanelProps> = props => {
  const dispatch = useDispatch()

  const {
    currentWorkspaceId,
    fullSpeakerVoiceSidePanel,
    speakerVoice,
    isNewSpeakerVoice,
    shouldRender,
  } = props

  const speakerVoices = useSelector(getSpeakerVoices)

  const halfSpeakerVoiceSidePanel = !fullSpeakerVoiceSidePanel && shouldRender
  const shouldRenderOverlay = halfSpeakerVoiceSidePanel

  const indexOfOpenedSpeakerVoice = speakerVoice ? speakerVoices.indexOf(speakerVoice) : -1
  const isFirstSpeakerVoice = indexOfOpenedSpeakerVoice < 0
  const isLastSpeakerVoice = speakerVoices.length === 0 || indexOfOpenedSpeakerVoice === speakerVoices.length - 1

  const speakerVoicesRef = useRef(speakerVoices)
  const indexOfOpenedSpeakerVoiceRef = useRef(indexOfOpenedSpeakerVoice)
  speakerVoicesRef.current = speakerVoices
  indexOfOpenedSpeakerVoiceRef.current = indexOfOpenedSpeakerVoice

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (fullSpeakerVoiceSidePanel) { return }
      if (e.target) {
        const element = e.target as HTMLElement
        if (hasElementOrSomeParentTheClass(element, 'SpeakerVoiceSidePanel__element')) {
          return
        }
        props.resetCurrentSpeakerVoice()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  const isArrowUp = (event: KeyboardEvent) => {
    return event.key === 'ArrowUp'
  }

  const isArrowDown = (event: KeyboardEvent) => {
    return event.key === 'ArrowDown'
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    if (isArrowUp(event)) {
      event.preventDefault()
      setPreviousSpeakerVoice()
    }

    if (isArrowDown(event)) {
      event.preventDefault()
      setNextSpeakerVoice()
    }
  }

  const setNextSpeakerVoice = () => {
    if (indexOfOpenedSpeakerVoiceRef.current < speakerVoicesRef.current.length - 1 && indexOfOpenedSpeakerVoiceRef.current >= -1) {
      props.setCurrentSpeakerVoice(speakerVoicesRef.current[indexOfOpenedSpeakerVoiceRef.current + 1])
    }
  }

  const setPreviousSpeakerVoice = () => {
    if (indexOfOpenedSpeakerVoiceRef.current > 0) {
      props.setCurrentSpeakerVoice(speakerVoicesRef.current[indexOfOpenedSpeakerVoiceRef.current - 1])
    }
  }

  const onSpeakerVoiceRemoval = (speakerVoiceId: string) => {
    if (speakerVoice && speakerVoice.id === speakerVoiceId) {
      if (!isLastSpeakerVoice) {
        setNextSpeakerVoice()
      } else if (!isFirstSpeakerVoice) {
        setPreviousSpeakerVoice()
      } else {
        props.resetCurrentSpeakerVoice()
      }
    }
    dispatch(removeSpeakerVoicesFromWorkspace({ workspaceId: currentWorkspaceId, speakerVoiceIds: [speakerVoice!.id] }))
  }

  const renderActionsHalfView = () => {
    return (
      <div className="SpeakerVoiceSidePanel__actions">
        <Close
          className="SpeakerVoiceSidePanel__actions-close"
          onClick={props.resetCurrentSpeakerVoice}
        />
        <Expand
          className="SpeakerVoiceSidePanel__actions-expand"
          onClick={props.setFullSpeakerVoiceSidePanel}
        />
        <div className="SpeakerVoiceSidePanel__actions-separator"/>
        <Next
          className={classnames(
            'SpeakerVoiceSidePanel__actions-next',
            { 'SpeakerVoiceSidePanel__actions-next-disabled': isLastSpeakerVoice }
          )}
          onClick={setNextSpeakerVoice}
        />
          <Previous
            className={classnames(
              'SpeakerVoiceSidePanel__actions-next',
              { 'SpeakerVoiceSidePanel__actions-previous-disabled': isFirstSpeakerVoice }
            )}
            onClick={setPreviousSpeakerVoice}
          />
      </div>
    )
  }

  const renderContent = () => {
    return (
      <SpeakerVoiceSidePanelContent
        fullSpeakerVoiceSidePanel={fullSpeakerVoiceSidePanel}
        workspaceId={currentWorkspaceId}
        speakerVoice={speakerVoice}
        isNewSpeakerVoice={isNewSpeakerVoice}
        resetCurrentSpeakerVoice={props.resetCurrentSpeakerVoice}
        resetIsNewCurrentSpeakerVoice={props.resetIsNewCurrentSpeakerVoice}
        onSpeakerVoiceRemoval={onSpeakerVoiceRemoval}
      />
    )
  }

  return (
    <React.Fragment>
      {shouldRenderOverlay && (
        <Overlay />
      )}
      <div className={classnames(
        'SpeakerVoiceSidePanel',
        'SpeakerVoiceSidePanel__element',
        { 'SpeakerVoiceSidePanel__hidden': !shouldRender },
        { 'SpeakerVoiceSidePanel__full': fullSpeakerVoiceSidePanel }
      )}>
        <div className="SpeakerVoiceSidePanel__scroll-container">
          {halfSpeakerVoiceSidePanel && renderActionsHalfView()}
          {shouldRender && renderContent()}
        </div>
      </div>
    </React.Fragment>
  )
}

export default SpeakerVoiceSidePanel
